import classNames from "classnames";
import React, { useCallback } from "react";
import { ModalContextProvider, useModal } from "../../services/ModalContext";
import { LBtn } from "./LBtn";
import styles from "./LModal.module.css";

const ModalHeader = ({ title }: { title: string | JSX.Element }): JSX.Element => {
    return <div className={styles.header}>{title}</div>;
};

const ModalOverlay = ({
    children,
    closeOnOverlay
}: {
    children: React.ReactNode;
    closeOnOverlay?: boolean;
}): JSX.Element => {
    const { closeModal } = useModal();

    return (
        <div
            className={styles.overlay}
            data-scroll-hint-modal
            onClick={() => {
                closeOnOverlay && closeModal();
            }}
        >
            {children}
        </div>
    );
};

const ModalContent = ({ children, className }: { children: React.ReactNode; className: string }): JSX.Element => {
    return (
        <div className={`${styles.content} ${className}`} onClick={(e) => e.stopPropagation()}>
            {children}
        </div>
    );
};

const ModalBody = ({
    children,
    fullHeight = true
}: {
    children: React.ReactNode;
    fullHeight?: boolean;
    fullWidth?: boolean;
}): JSX.Element => {
    const classes = classNames(styles.body, fullHeight ? styles.bodyFullHeight : "");

    return <div className={classes}>{children}</div>;
};

const ModalFooter = ({ children }: { children: React.ReactNode }): JSX.Element => {
    return <div className={styles.footer}>{children}</div>;
};

const CancelConfirm = ({
    onCancel,
    onConfirm,
    cancelText = "Cancel",
    confirmText = "Confirm",
    confirmLoading,
    cancelClasses,
    confirmClasses,
    confirmColor,
    divider = true
}: {
    onCancel: () => void;
    onConfirm: () => void;
    cancelText?: string;
    confirmText?: string;
    confirmLoading?: boolean;
    cancelClasses?: string;
    confirmClasses?: string;
    confirmColor?: string;
    divider?: boolean;
}): JSX.Element => {
    return (
        <>
            {divider ? <div className="divider" /> : null}
            <div className={`${styles.actions} pt-2 pb-5`}>
                <div className="columns">
                    <div className="column" />
                    <div className="column is-4">
                        {cancelText && (
                            <LBtn variation="outline" classes={cancelClasses} fullWidth onClick={onCancel}>
                                {cancelText}
                            </LBtn>
                        )}
                    </div>
                    <div className="column is-1" />
                    <div className="column is-4">
                        {confirmText && (
                            <LBtn
                                fullWidth
                                onClick={onConfirm}
                                classes={confirmClasses}
                                backgroundColor={confirmColor}
                                loading={confirmLoading}
                            >
                                {confirmText}
                            </LBtn>
                        )}
                    </div>
                    <div className="column" />
                </div>
            </div>
        </>
    );
};

export const LModal = ({
    onClose,
    onConfirm,
    title,
    children,
    closeOnOverlay,
    fullHeight,
    footer,
    header,
    width,
    hasCloseButton,
    hasCancelConfirmButtons,
    cancelText,
    confirmText,
    confirmLoading,
    cancelClasses,
    confirmClasses,
    divider,
    keepActionsVisibleOnMobileLandscape = false
}: LModalProps): JSX.Element => {
    const closeModal = useCallback(onClose, [onClose]);
    const confirmModal = useCallback(() => onConfirm?.(), [onConfirm]);

    const getWidthClass = (): string => {
        switch (width) {
            case "s":
                return styles.small;
            case "m":
                return styles.medium;
            default:
                return styles.large;
        }
    };

    return (
        <ModalContextProvider closeModal={closeModal} onConfirm={confirmModal}>
            <ModalOverlay closeOnOverlay={closeOnOverlay}>
                <ModalContent
                    className={classNames(
                        getWidthClass(),
                        keepActionsVisibleOnMobileLandscape && styles.landscapeActions
                    )}
                >
                    {hasCloseButton && (
                        <a
                            style={{ position: "fixed", right: "25%", paddingRight: "10px" }}
                            className="has-text-weight-bold"
                            onClick={closeModal}
                        >
                            X
                        </a>
                    )}
                    {title && <ModalHeader title={title} />}
                    {header}
                    <div className="is-flex is-flex-direction-column is-justify-content-space-between">
                        <ModalBody fullHeight={fullHeight}>{children}</ModalBody>
                        {hasCancelConfirmButtons && (
                            <CancelConfirm
                                onCancel={onClose}
                                onConfirm={() => onConfirm?.()}
                                cancelText={cancelText}
                                confirmText={confirmText}
                                confirmLoading={confirmLoading}
                                cancelClasses={cancelClasses}
                                confirmClasses={confirmClasses}
                                divider={divider}
                            />
                        )}
                        {footer && <ModalFooter>{footer}</ModalFooter>}
                    </div>
                </ModalContent>
            </ModalOverlay>
        </ModalContextProvider>
    );
};

interface LModalProps {
    title?: string | JSX.Element;
    header?: JSX.Element;
    onClose: () => void;
    onConfirm?: () => void;
    children?: React.ReactNode;
    closeOnOverlay?: boolean;
    fullHeight?: boolean;
    footer?: JSX.Element;
    hasCloseButton?: boolean;
    hasCancelConfirmButtons?: boolean;
    cancelText?: string;
    confirmText?: string;
    cancelClasses?: string;
    confirmClasses?: string;
    confirmColor?: string;
    confirmLoading?: boolean;
    divider?: boolean;
    width?: LModalWidth;
    keepActionsVisibleOnMobileLandscape?: boolean;
}

export type LModalWidth = "s" | "m" | "l";
